import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import { api, fileUrl, getAuthHeaders } from "../api";
import type { CaseRecord, FileKind } from "../types";
import { Button } from "./ui/Button";
import AuthImage from "./AuthImage";
import { renderPdfThumbnail } from "../lib/pdfThumbnail";
import { formatDate } from "../lib/formatDate";
import FilePreviewModal, { type PreviewFile } from "./FilePreviewModal";

// Files tab — single flat view of every file attached anywhere in the
// case. Pulls from two sources because the schema has two: legacy
// records.file_r2_key (the "primary" file on a record) and the newer
// attachments table. They're merged and deduped by R2 key so a file
// that exists in both places only shows once.

interface Props {
  caseId: string;
  records: CaseRecord[];
  onNavigateToRecord: (recordId: string) => void;
  onRecordUpdated: (record: CaseRecord) => void;
  onRefresh: () => void;
}

type FilterKind = "all" | "pdf" | "image" | "doc" | "audio";
type SortKey = "date" | "filename" | "type" | "size";

function mimeToKind(mime: string | null): FileKind {
  if (!mime) return "other";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("video/")) return "video";
  if (mime === "application/pdf") return "pdf";
  if (mime.includes("wordprocessingml")) return "docx";
  if (mime.includes("spreadsheetml")) return "xlsx";
  return "other";
}

function emojiForKind(kind: FileKind): string {
  switch (kind) {
    case "pdf":
      return "📄";
    case "docx":
      return "📝";
    case "xlsx":
      return "📊";
    case "image":
      return "🖼";
    case "audio":
      return "🎙";
    case "video":
      return "🎞";
    default:
      return "📎";
  }
}

function kindLabel(kind: FileKind): string {
  switch (kind) {
    case "pdf":
      return "PDF";
    case "docx":
      return "Document";
    case "xlsx":
      return "Spreadsheet";
    case "image":
      return "Image";
    case "audio":
      return "Audio";
    case "video":
      return "Video";
    default:
      return "Other";
  }
}

function formatBytes(n: number | null): string {
  if (n === null || !Number.isFinite(n) || n <= 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function matchesFilter(kind: FileKind, filter: FilterKind): boolean {
  switch (filter) {
    case "all":
      return true;
    case "pdf":
      return kind === "pdf";
    case "image":
      return kind === "image";
    case "doc":
      return kind === "docx" || kind === "xlsx";
    case "audio":
      return kind === "audio";
  }
}

function buildFileItems(
  records: CaseRecord[],
  sizeOverrides: Record<string, number>,
): PreviewFile[] {
  const out: PreviewFile[] = [];
  const seen = new Set<string>();
  for (const r of records) {
    const subject = r.subject?.trim() || "(no subject)";
    if (r.file_r2_key && r.file_name) {
      if (!seen.has(r.file_r2_key)) {
        seen.add(r.file_r2_key);
        const mime = r.file_mime_type ?? null;
        out.push({
          key: `primary:${r.id}`,
          source: "primary",
          attachmentId: null,
          recordId: r.id,
          recordSubject: subject,
          recordDate: r.record_date,
          r2Key: r.file_r2_key,
          filename: r.file_name,
          mimeType: mime,
          sizeBytes: sizeOverrides[r.file_r2_key] ?? null,
          kind: mimeToKind(mime),
        });
      }
    }
    for (const att of r.attachments ?? []) {
      if (seen.has(att.r2_key)) continue;
      seen.add(att.r2_key);
      const storedSize = att.size_bytes && att.size_bytes > 0 ? att.size_bytes : null;
      out.push({
        key: `attachment:${att.id}`,
        source: "attachment",
        attachmentId: att.id,
        recordId: r.id,
        recordSubject: subject,
        recordDate: r.record_date,
        r2Key: att.r2_key,
        filename: att.filename,
        mimeType: att.mime_type,
        sizeBytes: storedSize ?? sizeOverrides[att.r2_key] ?? null,
        kind: mimeToKind(att.mime_type),
      });
    }
  }
  return out;
}

function sortFiles(items: PreviewFile[], key: SortKey): PreviewFile[] {
  const out = items.slice();
  out.sort((a, b) => {
    switch (key) {
      case "date":
        return b.recordDate.localeCompare(a.recordDate);
      case "filename":
        return a.filename.localeCompare(b.filename, undefined, {
          sensitivity: "base",
        });
      case "type":
        return (
          a.kind.localeCompare(b.kind) ||
          a.filename.localeCompare(b.filename)
        );
      case "size":
        return (b.sizeBytes ?? -1) - (a.sizeBytes ?? -1);
    }
  });
  return out;
}

async function fetchBlob(url: string): Promise<Blob> {
  const headers = await getAuthHeaders();
  const res = await fetch(url, { credentials: "include", headers });
  if (!res.ok) throw new Error(`http_${res.status}`);
  return res.blob();
}

function uniqueZipName(taken: Set<string>, name: string): string {
  if (!taken.has(name)) {
    taken.add(name);
    return name;
  }
  const dot = name.lastIndexOf(".");
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  let n = 2;
  while (taken.has(`${base} (${n})${ext}`)) n++;
  const chosen = `${base} (${n})${ext}`;
  taken.add(chosen);
  return chosen;
}

function csvEscape(s: string): string {
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildManifestCsv(
  rows: {
    filename: string;
    recordDate: string;
    recordSubject: string;
    mime: string;
    size: number | null;
  }[],
): string {
  const header = "Filename,Record Date,Record Subject,File Type,Size (bytes)";
  const lines = rows.map((r) =>
    [
      csvEscape(r.filename),
      csvEscape(r.recordDate),
      csvEscape(r.recordSubject),
      csvEscape(r.mime),
      r.size == null ? "" : String(r.size),
    ].join(","),
  );
  return [header, ...lines].join("\n");
}

export default function FilesTab({
  caseId,
  records,
  onNavigateToRecord,
  onRecordUpdated,
  onRefresh,
}: Props) {
  const [filter, setFilter] = useState<FilterKind>("all");
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<null | "download" | "zip" | "delete">(null);
  // r2_key → size_bytes for files where the DB had no size; populated
  // by the one-shot backfill call below.
  const [sizeOverrides, setSizeOverrides] = useState<Record<string, number>>({});
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);

  const allFiles = useMemo(
    () => buildFileItems(records, sizeOverrides),
    [records, sizeOverrides],
  );
  const visible = useMemo(
    () => sortFiles(allFiles.filter((f) => matchesFilter(f.kind, filter)), sortKey),
    [allFiles, filter, sortKey],
  );

  // Drop stale selections after a delete or refresh.
  useEffect(() => {
    setSelected((prev) => {
      const visibleKeys = new Set(allFiles.map((f) => f.key));
      const next = new Set<string>();
      for (const k of prev) if (visibleKeys.has(k)) next.add(k);
      return next;
    });
  }, [allFiles]);

  // One-shot backfill: ask the worker to R2-head any files whose size
  // we don't know. Persisted server-side for attachments; primary
  // files come back in sizeOverrides each session. Re-runs only when
  // new files appear with unknown sizes.
  const requestedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const needed = allFiles
      .filter((f) => f.sizeBytes === null)
      .map((f) => f.r2Key)
      .filter((k) => !requestedRef.current.has(k));
    if (needed.length === 0) return;
    for (const k of needed) requestedRef.current.add(k);
    void (async () => {
      try {
        const { sizes } = await api.backfillFileSizes(caseId, needed);
        if (Object.keys(sizes).length === 0) return;
        setSizeOverrides((prev) => ({ ...prev, ...sizes }));
      } catch {
        // Size is a nice-to-have — leave the dash on failure.
      }
    })();
  }, [allFiles, caseId]);

  const allVisibleSelected =
    visible.length > 0 && visible.every((f) => selected.has(f.key));

  function toggleOne(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleAll() {
    if (allVisibleSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        for (const f of visible) next.delete(f.key);
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        for (const f of visible) next.add(f.key);
        return next;
      });
    }
  }

  const downloadOne = useCallback(
    async (item: PreviewFile) => {
      setBusy("download");
      try {
        const blob = await fetchBlob(fileUrl(caseId, item.r2Key));
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = item.filename || "download";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        window.alert(`Couldn't download: ${(err as Error).message}`);
      } finally {
        setBusy(null);
      }
    },
    [caseId],
  );

  async function downloadMany(items: PreviewFile[], zipName: string) {
    if (items.length === 0) return;
    if (items.length === 1) {
      await downloadOne(items[0]!);
      return;
    }
    setBusy("zip");
    try {
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      const taken = new Set<string>();
      const manifestRows: Parameters<typeof buildManifestCsv>[0] = [];
      for (const item of items) {
        try {
          const blob = await fetchBlob(fileUrl(caseId, item.r2Key));
          const name = uniqueZipName(taken, item.filename || "file");
          zip.file(name, blob);
          manifestRows.push({
            filename: name,
            recordDate: item.recordDate,
            recordSubject: item.recordSubject,
            mime: item.mimeType ?? "",
            size: item.sizeBytes,
          });
        } catch (err) {
          manifestRows.push({
            filename: `${item.filename} (FAILED: ${(err as Error).message})`,
            recordDate: item.recordDate,
            recordSubject: item.recordSubject,
            mime: item.mimeType ?? "",
            size: item.sizeBytes,
          });
        }
      }
      zip.file("manifest.csv", buildManifestCsv(manifestRows));
      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = zipName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      window.alert(`Couldn't build archive: ${(err as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  const deleteOne = useCallback(
    async (item: PreviewFile) => {
      if (!window.confirm("Delete this file? The record will be kept.")) return;
      setBusy("delete");
      try {
        if (item.source === "attachment" && item.attachmentId) {
          await api.deleteAttachment(item.attachmentId);
        } else {
          const { record } = await api.clearRecordFile(item.recordId);
          onRecordUpdated(record);
        }
        onRefresh();
        // Close preview if the deleted item was the one open.
        setPreviewIndex((cur) =>
          cur !== null && visible[cur]?.key === item.key ? null : cur,
        );
      } catch (err) {
        window.alert(`Couldn't delete: ${(err as Error).message}`);
      } finally {
        setBusy(null);
      }
    },
    [onRecordUpdated, onRefresh, visible],
  );

  async function renameFile(item: PreviewFile, nextName: string) {
    const trimmed = nextName.trim();
    if (!trimmed || trimmed === item.filename) {
      setRenaming(null);
      return;
    }
    try {
      if (item.source === "attachment" && item.attachmentId) {
        await api.renameAttachment(item.attachmentId, trimmed);
      } else {
        const { record } = await api.renameRecordFile(item.recordId, trimmed);
        onRecordUpdated(record);
      }
      onRefresh();
    } catch (err) {
      window.alert(`Couldn't rename: ${(err as Error).message}`);
    } finally {
      setRenaming(null);
    }
  }

  function openPreview(item: PreviewFile) {
    const i = visible.findIndex((f) => f.key === item.key);
    if (i >= 0) setPreviewIndex(i);
  }

  function activateRow(item: PreviewFile) {
    // Match the per-kind behaviour spec'd for click-to-open. The
    // preview modal handles image/PDF/audio in-place; "other" kinds
    // (docx, xlsx, video, unknown) fall through to a direct download
    // because there's no inline preview that would add value.
    if (item.kind === "image" || item.kind === "pdf" || item.kind === "audio") {
      openPreview(item);
    } else {
      void downloadOne(item);
    }
  }

  const selectedFiles = visible.filter((f) => selected.has(f.key));

  return (
    <div className="files-tab">
      <div className="files-toolbar">
        <label className="files-select-all">
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={toggleAll}
            aria-label="Select all visible files"
          />
          <span>
            {selectedFiles.length > 0
              ? `${selectedFiles.length} selected`
              : "Select all"}
          </span>
        </label>

        <label className="files-control">
          Sort by{" "}
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
          >
            <option value="date">Date</option>
            <option value="filename">Filename</option>
            <option value="type">File type</option>
            <option value="size">Size</option>
          </select>
        </label>

        <label className="files-control">
          Filter{" "}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterKind)}
          >
            <option value="all">All</option>
            <option value="pdf">PDFs</option>
            <option value="image">Images</option>
            <option value="doc">Documents</option>
            <option value="audio">Audio</option>
          </select>
        </label>

        <span className="files-toolbar-spacer" />

        {selectedFiles.length > 0 && (
          <Button
            variant="secondary"
            size="sm"
            disabled={busy !== null}
            onClick={() =>
              downloadMany(selectedFiles, `files-selected-${caseId}.zip`)
            }
          >
            {busy === "zip"
              ? "Zipping…"
              : selectedFiles.length === 1
                ? "⬇ Download"
                : `⬇ Download selected (${selectedFiles.length})`}
          </Button>
        )}

        <Button
          variant="secondary"
          size="sm"
          disabled={busy !== null || visible.length === 0}
          onClick={() => downloadMany(visible, `files-all-${caseId}.zip`)}
        >
          ⬇ Download all
        </Button>
      </div>

      {visible.length === 0 ? (
        <p className="files-empty">
          {allFiles.length === 0
            ? "No files yet. Files appear here when you upload attachments or record calls."
            : "No files match the current filter."}
        </p>
      ) : (
        <div className="files-grid" role="table" aria-label="Files">
          <div className="files-grid-head" role="row">
            <div role="columnheader" className="files-col-check">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={toggleAll}
                aria-label="Select all visible files"
              />
            </div>
            <div role="columnheader" className="files-col-thumb" aria-hidden />
            <div role="columnheader" className="files-col-name">
              Filename
            </div>
            <div role="columnheader" className="files-col-type">
              Type
            </div>
            <div role="columnheader" className="files-col-size">
              Size
            </div>
            <div role="columnheader" className="files-col-date">
              Date
            </div>
            <div role="columnheader" className="files-col-rec">
              Source record
            </div>
            <div role="columnheader" className="files-col-actions">
              Actions
            </div>
          </div>

          {visible.map((item) => (
            <FileRow
              key={item.key}
              caseId={caseId}
              item={item}
              checked={selected.has(item.key)}
              busy={busy !== null}
              renaming={renaming === item.key}
              onStartRename={() => setRenaming(item.key)}
              onCancelRename={() => setRenaming(null)}
              onCommitRename={(name) => renameFile(item, name)}
              onToggle={() => toggleOne(item.key)}
              onActivate={() => activateRow(item)}
              onDownload={() => downloadOne(item)}
              onDelete={() => deleteOne(item)}
              onOpenRecord={() => onNavigateToRecord(item.recordId)}
            />
          ))}
        </div>
      )}

      {previewIndex !== null && (
        <FilePreviewModal
          caseId={caseId}
          files={visible}
          index={previewIndex}
          onIndexChange={setPreviewIndex}
          onClose={() => setPreviewIndex(null)}
          onDownload={downloadOne}
          onDelete={deleteOne}
          onOpenRecord={(recordId) => {
            setPreviewIndex(null);
            onNavigateToRecord(recordId);
          }}
        />
      )}
    </div>
  );
}

interface RowProps {
  caseId: string;
  item: PreviewFile;
  checked: boolean;
  busy: boolean;
  renaming: boolean;
  onStartRename: () => void;
  onCancelRename: () => void;
  onCommitRename: (name: string) => void;
  onToggle: () => void;
  onActivate: () => void;
  onDownload: () => void;
  onDelete: () => void;
  onOpenRecord: () => void;
}

function FileRow({
  caseId,
  item,
  checked,
  busy,
  renaming,
  onStartRename,
  onCancelRename,
  onCommitRename,
  onToggle,
  onActivate,
  onDownload,
  onDelete,
  onOpenRecord,
}: RowProps) {
  const url = fileUrl(caseId, item.r2Key);

  // The row itself is clickable. Anything inside the row that has its
  // own interactive role (checkbox, buttons, rename input, source-record
  // link) calls stopPropagation so we don't double-trigger.
  function handleRowClick(e: MouseEvent<HTMLDivElement>) {
    if (renaming) return;
    // Defensive: if the click bubbled from an interactive child whose
    // handler forgot stopPropagation, bail rather than re-firing.
    const tgt = e.target as HTMLElement;
    if (tgt.closest("button, input, a")) return;
    onActivate();
  }

  function handleRowKey(e: KeyboardEvent<HTMLDivElement>) {
    if (renaming) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onActivate();
    }
  }

  return (
    <div
      className="files-grid-row"
      role="row"
      tabIndex={0}
      onClick={handleRowClick}
      onKeyDown={handleRowKey}
    >
      <div role="cell" className="files-col-check">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${item.filename}`}
        />
      </div>
      <div role="cell" className="files-col-thumb">
        <FileThumb caseId={caseId} item={item} url={url} />
      </div>
      <div role="cell" className="files-col-name">
        {renaming ? (
          <RenameInput
            initial={item.filename}
            onCommit={onCommitRename}
            onCancel={onCancelRename}
          />
        ) : (
          <span className="files-name-wrap">
            <span className="files-name-text" title={item.filename}>
              {item.filename}
            </span>
            <button
              type="button"
              className="files-rename-btn"
              onClick={(e) => {
                e.stopPropagation();
                onStartRename();
              }}
              aria-label={`Rename ${item.filename}`}
              title="Rename"
            >
              ✎
            </button>
          </span>
        )}
      </div>
      <div role="cell" className="files-col-type">
        {kindLabel(item.kind)}
      </div>
      <div role="cell" className="files-col-size">
        {formatBytes(item.sizeBytes)}
      </div>
      <div role="cell" className="files-col-date">
        {formatDate(item.recordDate, "short")}
      </div>
      <div role="cell" className="files-col-rec">
        <button
          type="button"
          className="files-row-subject"
          onClick={(e) => {
            e.stopPropagation();
            onOpenRecord();
          }}
          title="Open this record"
        >
          {item.recordSubject}
        </button>
      </div>
      <div role="cell" className="files-col-actions">
        <Button
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onDownload();
          }}
        >
          Download
        </Button>
        <Button
          variant="danger"
          size="sm"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          Delete
        </Button>
      </div>
    </div>
  );
}

function RenameInput({
  initial,
  onCommit,
  onCancel,
}: {
  initial: string;
  onCommit: (name: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    // Select the basename so the user can type a new name without
    // wiping the extension. If there's no dot, select the whole thing.
    const dot = initial.lastIndexOf(".");
    if (dot > 0) el.setSelectionRange(0, dot);
    else el.select();
  }, [initial]);

  return (
    <input
      ref={ref}
      type="text"
      className="files-rename-input"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") onCommit(value);
        else if (e.key === "Escape") onCancel();
      }}
      onBlur={() => onCommit(value)}
      aria-label="New filename"
    />
  );
}

function FileThumb({
  caseId,
  item,
  url,
}: {
  caseId: string;
  item: PreviewFile;
  url: string;
}) {
  const [pdfThumb, setPdfThumb] = useState<string | null>(null);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    if (item.kind !== "pdf") return;
    (async () => {
      try {
        const blob = await fetchBlob(url);
        if (cancelled.current) return;
        const bytes = await blob.arrayBuffer();
        if (cancelled.current) return;
        const dataUrl = await renderPdfThumbnail(bytes, 80);
        if (!cancelled.current && dataUrl) setPdfThumb(dataUrl);
      } catch {
        /* fall back to icon */
      }
    })();
    return () => {
      cancelled.current = true;
    };
  }, [item.kind, url]);

  void caseId;

  if (item.kind === "image") {
    return (
      <AuthImage src={url} alt={item.filename} className="files-row-thumb" />
    );
  }
  if (item.kind === "pdf" && pdfThumb) {
    return (
      <img
        src={pdfThumb}
        alt={item.filename}
        className="files-row-thumb"
        loading="lazy"
      />
    );
  }
  return (
    <div className="files-row-thumb files-row-thumb-icon" aria-hidden>
      {emojiForKind(item.kind)}
    </div>
  );
}
